import { useState, useEffect } from 'react';
import { isDevEnv } from '../services/api';
import PublicHeader from '../components/PublicHeader';
import SiteFooter from '../components/SiteFooter';
import { fetchContactSettings, submitContactForm } from '../services/api';

const CONTACT_TYPES = [
  { value: 'question', label: 'Question' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'support', label: 'Support' },
  { value: 'other', label: 'Other' },
];

export default function ContactPage() {
  // Contact settings
  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [settingsError, setSettingsError] = useState(null);

  // Email reveal (existing functionality)
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const local = 'support';
  const domain = 'clipto.tv';
  const emailConst = `${local}@${domain}`;

  // Form state
  const [formData, setFormData] = useState({
    type: 'question',
    subject: '',
    message: '',
    email: '',
    website: '', // honeypot
  });
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Fetch contact settings on mount
  useEffect(() => {
    async function loadSettings() {
      try {
        setLoadingSettings(true);
        const data = await fetchContactSettings();
        // Helpful debug output when developing locally to confirm server response
        try {
          // eslint-disable-next-line no-console
          console.debug('ContactPage: fetched contact settings', data);
        } catch (e) {}
        setSettings(data);
        setSettingsError(null);
      } catch (err) {
        console.error('Failed to load contact settings:', err);
        setSettingsError('Failed to load contact settings');
        // Conservative default: show form but hide email when settings cannot be loaded
        setSettings({ show_form: true, show_email: false, email: '' });
      } finally {
        setLoadingSettings(false);
      }
    }

    loadSettings();
  }, []);

  async function handleCopy() {
    try {
      /* c8 ignore start */
      const toCopy = settings?.email || emailConst;
      /* c8 ignore stop */
      await navigator.clipboard.writeText(toCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // ignore
    }
  }

  function validateForm() {
    const newErrors = {};

    if (!formData.type) {
      newErrors.type = 'Type is required';
    }

    if (!formData.subject || formData.subject.length < 3) {
      newErrors.subject = 'Subject must be at least 3 characters';
    }

    if (!formData.message || formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    /* c8 ignore start */
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    /* c8 ignore stop */

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmitForm(e) {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    if (!validateForm()) {
      return;
    }

    // Don't submit if honeypot is filled (silent fail)
    if (formData.website) {
      setSubmitSuccess(true);
      setFormData({ type: 'question', subject: '', message: '', email: '', website: '' });
      return;
    }

    try {
      setSubmitLoading(true);
      const result = await submitContactForm({
        type: formData.type,
        subject: formData.subject,
        message: formData.message,
        email: formData.email || undefined,
        website: formData.website || undefined,
      });

      if (result.success) {
        setSubmitSuccess(true);
        setFormData({ type: 'question', subject: '', message: '', email: '', website: '' });
        setErrors({});
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        setSubmitError('Failed to submit message. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      /* c8 ignore start */
      setSubmitError(err.message || 'Failed to submit message. Please try again.');
      /* c8 ignore stop */
    } finally {
      setSubmitLoading(false);
    }
  }

  // Loading state
  if (loadingSettings) {
    return (
      <div className="min-h-screen bg-dark-900 flex flex-col">
        <PublicHeader />
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <p className="text-gray-400">Loading...</p>
        </main>
        <SiteFooter />
      </div>
    );
  }

  // Both disabled state
  if (!settings?.show_form && !settings?.show_email) {
    return (
      <div className="min-h-screen bg-dark-900 flex flex-col">
        <PublicHeader />
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="max-w-md w-full mx-auto text-center">
            <h1 className="text-3xl font-bold text-white mb-2">Contact</h1>
            <p className="text-gray-400">
              Contact is currently unavailable. Please try again later.
            </p>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-900 flex flex-col">
      <PublicHeader />

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full mx-auto">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-white mb-2">
              <span role="img" aria-label="call">🤙</span> Contact
            </h1>
            <p className="text-gray-400">Have a question, issue, or feedback?</p>
            <p className="text-gray-400 mt-2">Reach out and we'll get back to you.</p>
          </div>

          <div className="p-6 bg-dark-800 rounded-xl border border-dark-600 shadow-sm">
            {/* Email Section */}
                {settings?.show_email && (
              <>
                <p className="text-sm text-gray-500 uppercase tracking-wider mb-4">Email</p>
                {!revealed ? (
                  <div className="flex flex-col items-center gap-3 mb-8">
                    <p className="text-gray-400 text-center whitespace-nowrap">
                      Hidden to prevent automated scraping.
                    </p>
                    <button
                      onClick={() => setRevealed(true)}
                      className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover whitespace-nowrap"
                    >
                      Reveal
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3 mb-8">
                        {/** Use the fetched email when available, otherwise fall back to the default constant */}
                        {/* c8 ignore start */}
                        <a
                          href={`mailto:${settings?.email || emailConst}`}
                          className="text-primary hover:underline text-lg font-medium"
                        >
                          {settings?.email || emailConst}
                        </a>
                        {/* c8 ignore stop */}
                    <button
                          onClick={handleCopy}
                      className="px-3 py-1 text-sm bg-dark-700 hover:bg-dark-600 text-gray-300 rounded"
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                )}
                
                {settings?.show_form && <hr className="border-dark-600 mb-8" />}
              </>
            )}

            {/* Contact Form Section */}
            {settings?.show_form && (
              <>
                <p className="text-sm text-gray-500 uppercase tracking-wider mb-4">Send a Message</p>

                {submitSuccess && (
                  <div className="mb-4 p-3 bg-green-900/20 border border-green-700/50 rounded text-green-400 text-sm">
                    Message sent successfully! We'll get back to you soon.
                  </div>
                )}

                {submitError && (
                  <div className="mb-4 p-3 bg-red-900/20 border border-red-700/50 rounded text-red-400 text-sm">
                    {submitError}
                  </div>
                )}

                <form onSubmit={handleSubmitForm} className="space-y-4">
                  {/* Honeypot field - hidden via CSS */}
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    style={{ display: 'none' }}
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                  />

                  {/* Type Select */}
                  <div>
                    <label htmlFor="contact-type" className="block text-sm font-medium text-gray-300 mb-1">
                      Type <span className="text-red-400">*</span>
                    </label>
                    <select
                      id="contact-type"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className={`w-full px-3 py-2 bg-dark-700 border rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary ${
                        errors.type ? 'border-red-500' : 'border-dark-600'
                      }`}
                    >
                      <option value="">Select a type...</option>
                      {CONTACT_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {errors.type && <p className="text-red-400 text-sm mt-1">{errors.type}</p>}
                  </div>

                  {/* Subject Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Subject <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Brief subject line"
                      className={`w-full px-3 py-2 bg-dark-700 border rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary ${
                        errors.subject ? 'border-red-500' : 'border-dark-600'
                      }`}
                    />
                    {errors.subject && <p className="text-red-400 text-sm mt-1">{errors.subject}</p>}
                  </div>

                  {/* Message Textarea */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Message <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us more details..."
                      rows="4"
                      className={`w-full px-3 py-2 bg-dark-700 border rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary resize-none ${
                        errors.message ? 'border-red-500' : 'border-dark-600'
                      }`}
                    />
                    {errors.message && <p className="text-red-400 text-sm mt-1">{errors.message}</p>}
                  </div>

                  {/* Email Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Email <span className="text-gray-500">(optional)</span>
                    </label>
                    {/* c8 ignore start */}
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      className={`w-full px-3 py-2 bg-dark-700 border rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary ${
                        errors.email ? 'border-red-500' : 'border-dark-600'
                      }`}
                    />
                    {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
                    {/* c8 ignore stop */}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitLoading}
                    className="w-full mt-6 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {submitLoading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
